import { Select as BaseSelect } from '@base-ui/react/select';
import type { ComponentProps } from 'react';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '../lib/icons.tsx';
import { LAYER } from '../lib/layer.ts';
import { cx, styled } from '../lib/styled.tsx';
import {
  groupLabelSurface,
  itemChoice,
  itemSurface,
  popupSeparator,
  popupSurface,
  popupTransition,
} from '../lib/surfaces.ts';

/** `--anchor-width` keeps the list at least as wide as the trigger it came from. */
const popup =
  `${popupSurface} ${popupTransition} ` +
  'min-w-[var(--anchor-width)] max-h-[var(--available-height)] p-1';

function Icon({ children, className, ...props }: ComponentProps<typeof BaseSelect.Icon>) {
  return (
    <BaseSelect.Icon className={cx('flex shrink-0 text-ink-muted', className)} {...props}>
      {children ?? <ChevronDownIcon className="size-4" />}
    </BaseSelect.Icon>
  );
}

function ItemIndicator({
  children,
  className,
  ...props
}: ComponentProps<typeof BaseSelect.ItemIndicator>) {
  return (
    <BaseSelect.ItemIndicator
      className={cx('flex size-4 shrink-0 items-center justify-center text-brand-600', className)}
      {...props}
    >
      {children ?? <CheckIcon className="size-3.5" />}
    </BaseSelect.ItemIndicator>
  );
}

const scrollArrow =
  'z-1 flex h-6 w-full cursor-default items-center justify-center bg-surface-raised text-ink-muted data-[visible=false]:hidden';

function ScrollUpArrow({
  children,
  className,
  ...props
}: ComponentProps<typeof BaseSelect.ScrollUpArrow>) {
  return (
    <BaseSelect.ScrollUpArrow className={cx(scrollArrow, 'rounded-t-card', className)} {...props}>
      {children ?? <ChevronUpIcon className="size-3.5" />}
    </BaseSelect.ScrollUpArrow>
  );
}

function ScrollDownArrow({
  children,
  className,
  ...props
}: ComponentProps<typeof BaseSelect.ScrollDownArrow>) {
  return (
    <BaseSelect.ScrollDownArrow className={cx(scrollArrow, 'rounded-b-card', className)} {...props}>
      {children ?? <ChevronDownIcon className="size-3.5" />}
    </BaseSelect.ScrollDownArrow>
  );
}

/**
 * Choosing one option (or several, with `multiple`) from a known list.
 *
 * Kept as parts because the row is where the variation lives — an icon, a
 * description under the label, a group header. Pass `items` on the root when the
 * trigger has to show a label for a value the list has not rendered yet.
 */
export const Select = {
  /** State only — renders no element of its own. */
  Root: BaseSelect.Root,
  Label: styled(BaseSelect.Label, 'text-sm font-medium text-ink'),
  Trigger: styled(
    BaseSelect.Trigger,
    'flex h-10 w-full items-center justify-between gap-2 rounded-control ' +
      'border border-border-subtle bg-surface px-3 text-sm text-ink select-none ' +
      'focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/25 ' +
      'data-invalid:border-danger data-invalid:ring-danger/20 ' +
      'data-disabled:cursor-not-allowed data-disabled:bg-surface-muted data-disabled:opacity-60',
  ),
  Value: styled(BaseSelect.Value, 'truncate text-left data-placeholder:text-ink-muted'),
  Icon,
  Portal: BaseSelect.Portal,
  Backdrop: styled(BaseSelect.Backdrop, 'fixed inset-0'),
  Positioner: styled(BaseSelect.Positioner, `${LAYER.popup} outline-none`, { sideOffset: 6 }),
  Popup: styled(BaseSelect.Popup, popup),
  Arrow: styled(BaseSelect.Arrow, ''),
  List: styled(BaseSelect.List, 'max-h-[inherit] overflow-y-auto outline-none'),
  Item: styled(BaseSelect.Item, `${itemSurface} justify-between ${itemChoice}`),
  ItemText: styled(BaseSelect.ItemText, 'truncate'),
  ItemIndicator,
  Separator: styled(BaseSelect.Separator, popupSeparator),
  Group: styled(BaseSelect.Group, ''),
  GroupLabel: styled(BaseSelect.GroupLabel, groupLabelSurface),
  ScrollUpArrow,
  ScrollDownArrow,
};
